import React, { useState } from 'react';
import {
  IonButton,
  IonList,
  IonItem,
  IonLabel,
  IonText,
  IonContent,
  useIonViewWillEnter,
  useIonViewWillLeave,
} from '@ionic/react';
import { useHistory, useParams } from 'react-router-dom';
import { PageLayout } from 'containers/layout';
import OrderActions from 'redux/OrderRedux';
import { useDispatch, useSelector } from 'react-redux';
import AppActions from 'redux/AppRedux';
import api_v2 from 'services/api_v2';
import { AuthSelectors } from 'redux/AuthRedux';
import { BarcodeScanner } from '@capacitor-mlkit/barcode-scanning';
import QrReader from 'modern-react-qr-reader';
import { isMobileVersion, isWebVersion } from 'utils/platform';

function SendLocationPage() {
  const params = useParams();
  const dispatch = useDispatch();
  const history = useHistory();

  const token = useSelector(AuthSelectors.selectToken);

  const [isScanning, setScanning] = useState(false);
  const [permission, setPermission] = useState(false);
  const [locationId, setLocationId] = useState();

  const venueId = params.locationId;

  const requestPermissions = async () => {
    const { camera } = await BarcodeScanner.requestPermissions();
    if (camera === 'granted') {
      // the user granted permission
      setPermission(true);
    }
  };

  const checkPermission = async () => {
    if (isMobileVersion) {
      // check or request permission
      const { camera } = await BarcodeScanner.checkPermissions();

      if (camera === 'granted') {
        // the user granted permission
        setPermission(true);
      } else if (camera === 'denied') {
        console.log('Camera access denied.');
        AppActions.setToast('Camera access denied.', 'danger');
      } else {
        setPermission(false);
        requestPermissions();
        return false;
      }
    }
  };

  useIonViewWillEnter(() => {
    checkPermission();
  });

  const stopScan = async () => {
    if (isWebVersion) {
      setScanning(false);
      return;
    }

    setScanning(false);
    document.querySelector('body').classList.remove('scanner-active');

    await BarcodeScanner.removeAllListeners();
    await BarcodeScanner.stopScan();
  };

  useIonViewWillLeave(() => {
    stopScan();
  }, [isScanning]);

  const scanSingleBarcode = async () => {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve) => {
      document.querySelector('body').classList.add('scanner-active');

      const listener = await BarcodeScanner.addListener(
        'barcodeScanned',
        async (result) => {
          await listener.remove();
          resolve(result.barcode);
        }
      );

      await BarcodeScanner.startScan();
    });
  };

  const makeCall = async (result) => {
    stopScan();
    try {
      const api = api_v2(token);
      const locationIdResult = await api.getLocationIdFromQR(result);

      setLocationId(locationIdResult.locationId);
      dispatch(OrderActions.sendOrder(venueId, locationIdResult.locationId));
    } catch (error) {
      AppActions.setToast(
        'The locationId does not exist. Please try to scan again.',
        'danger'
      );
    }
  };

  const startScan = async () => {
    if (isMobileVersion && permission && !isScanning) {
      setScanning(true);
      const result = await scanSingleBarcode();
      makeCall(result.rawValue);
    } else if (isWebVersion && !isScanning) {
      setScanning(true);
    } else {
      console.log('camera permission denied.');
    }
  };

  const onScanAction = () => {
    if (process.env.REACT_APP_QR_CODE_SKIP) {
      setLocationId('location10');
      dispatch(OrderActions.sendOrder(venueId, 'location10'));
    } else {
      startScan();
    }
  };

  const onViewCheckAction = () => {
    history.push(`/locations/${venueId}/tab`);
    setLocationId(null); // Reset locationId upon successful completion
  };

  return isMobileVersion ||
    (isWebVersion && !isScanning) ||
    process.env.REACT_APP_QR_CODE_SKIP ? (
      <PageLayout
        title="Send Order"
        back
        footer={
        locationId && (
          <IonButton
            color="tertiary"
            expand="block"
            fill="solid"
            onClick={onViewCheckAction}
          >
            VIEW CHECK
          </IonButton>
        )
      }
      >
        <IonContent
          className={isScanning ? 'barcode-scan-bg-hide' : 'barcode-scan-bg-show'}
        >
          <IonList>
            <IonItem>
              <IonLabel className="ion-text-wrap">
                <span>
                  <b>{locationId ? 'SUCCESS' : 'FINAL STEP'}</b>
                </span>
              </IonLabel>
            </IonItem>

            <IonItem lines="none" className="mt-5x">
              <IonLabel className="ion-text-wrap d-flex-content">
                <IonText
                  color="primary"
                  className="d-flex-center text-align-center"
                >
                  <h3>
                    {locationId
                    ? 'Your order has been sent'
                    : 'Scan QR code to send your order.'}
                  </h3>
                </IonText>

                {locationId ? (
                  <IonText color="primary" className="d-flex-center">
                    <h6>
                      <b>LOCATION: {locationId}</b>
                    </h6>
                  </IonText>
              ) : (
                <IonText
                  color="primary"
                  className="d-flex-center text-align-center mt-3x"
                >
                  <h6>
                    This will send your order to the kitchen and let them know
                    where to deliver your items.
                  </h6>
                </IonText>
              )}
              </IonLabel>
            </IonItem>

            {locationId ? (
              <IonItem lines="none" className="mt-3x">
                <IonLabel className="ion-text-wrap d-flex-content">
                  <IonText
                    color="primary"
                    className="d-flex-center text-align-center"
                  >
                    <h6>
                      Please stay at this location until your order has been
                      delivered.
                    </h6>
                  </IonText>
                </IonLabel>
              </IonItem>
          ) : (
            <IonItem lines="none" className="mt-5x">
              <div className="d-flex-row-center">
                <IonButton
                  color="secondary"
                  expand="block"
                  fill="solid"
                  type="submit"
                  className="scan-btn"
                  onClick={onScanAction}
                >
                  SCAN QR CODE
                </IonButton>
              </div>
            </IonItem>
          )}
          </IonList>
        </IonContent>
      </PageLayout>
  ) : (
    <QrReader
      delay={300}
      facingMode="environment"
      onError={(error) => {
        if (!!error) {
          console.log(error);
          if (
            error.message?.includes('Permission denied') ||
            error.message?.includes('user denied permission')
          ) {
            AppActions.setToast('camera permission denied.', 'danger');
            setScanning(false);
          }
        }
      }}
      onScan={(data) => {
        if (!!data) {
          makeCall(data);
        }
      }}
      style={{ width: '100%' }}
      constraints={{
        facingMode: 'environment',
      }}
    />
  );
}

export default SendLocationPage;
